"use client";

import { StoreApi, UseBoundStore } from "zustand";

import { defaultSampleRateHz } from "@/constants/defaultValues";
import { createConversionFullAction } from "@/data/conversion/actions/createConversionFullAction/createConversionFullAction";
import { IndividualConversion } from "@/types/defs/individualConversion";
import { AudioInputState, UserNoDates } from "@/types/types";
import { isAudioInputActive } from "@/utils/audioInput/is/isAudioInputActive";
import { isSelectedFile } from "@/utils/selectedFileCover/is/isSelectedFile";
import { stringifyDatesSelectedFileCovers } from "@/utils/serverClientSerialization/stringifyDatesSelectedFileCovers";
import { isValidLengthSubscription } from "@/utils/subscriptionLevels/time/isValidLengthSubscription";

export const handleSubmitConversion = async ({
  individualConversions,

  clearInput,
  user,

  conversionStrength,

  removeInstrumental,
  removeBackgroundVocals,
  deReverbDeEcho,
  deNoise,

  conversionGroupId,

  useConversionInputs,
}: {
  individualConversions: IndividualConversion[];

  clearInput: () => void;
  user: UserNoDates;

  conversionStrength: number;

  removeInstrumental: boolean;
  removeBackgroundVocals: boolean;
  deReverbDeEcho: boolean;
  deNoise: boolean;

  conversionGroupId: string | null;

  useConversionInputs: UseBoundStore<StoreApi<AudioInputState>>[];
}) => {
  const conversionInputStates = await new Promise<AudioInputState[]>(
    (resolve) => {
      // Wait for all uploaded audio files to finish uploading
      const checkIfDoneUploading = () => {
        let done = true;
        const audioInputStates = useConversionInputs.map((useConversionInput) =>
          useConversionInput.getState()
        );
        for (const { selectedFileCover } of audioInputStates) {
          if (
            selectedFileCover !== null &&
            isSelectedFile(selectedFileCover) &&
            selectedFileCover.progress !== null
          ) {
            done = false;
            break;
          }
        }

        if (done) {
          unsubscribes.forEach((unsubscribe) => unsubscribe());
          resolve(audioInputStates);
        }
      };

      const unsubscribes = useConversionInputs.map((useConversionInput) =>
        useConversionInput.subscribe(() => checkIfDoneUploading())
      );

      // Initial check in case they are already done
      checkIfDoneUploading();
    }
  );

  // Check if all selected file covers are null in that case return
  const allSelectedFileCoversAreNull = conversionInputStates.every(
    ({ selectedFileCover }) => selectedFileCover === null
  );
  if (allSelectedFileCoversAreNull) {
    return;
  }

  // Sort by fill order, not slot index — the backend derives createdAt from the array index, so this keeps results ordered like the input list.
  const activeSelectedFileCovers = conversionInputStates
    .filter(isAudioInputActive)
    .sort((first, second) => first.inputOrder - second.inputOrder)
    .map(({ selectedFileCover }) => selectedFileCover);

  await createConversionFullAction({
    individualConversions: individualConversions,

    conversionStrength: conversionStrength,

    removeInstrumental: removeInstrumental,
    removeBackgroundVocals: removeBackgroundVocals,
    deReverbDeEcho: deReverbDeEcho,
    deNoise: deNoise,

    selectedFileCovers: stringifyDatesSelectedFileCovers(
      activeSelectedFileCovers
    ),

    conversionGroupId: conversionGroupId,

    sampleRateHz: defaultSampleRateHz,
  });

  // Check if the user has enough time left in their subscription
  const numberOfIndividualConversions = individualConversions.length;
  let lenghtSAll = 0;

  for (const selectedFileCover of activeSelectedFileCovers) {
    if (isSelectedFile(selectedFileCover)) {
      if (selectedFileCover.selectedSound !== null) {
        lenghtSAll +=
          selectedFileCover.selectedSound.lengthS *
          numberOfIndividualConversions;
      }
    } else {
      lenghtSAll +=
        selectedFileCover.audioLength * numberOfIndividualConversions;
    }
  }

  // Check if the user has enough time left in their subscription
  if (user.conversionTimeLeftSeconds !== null) {
    const validAudioLength = isValidLengthSubscription(
      user.conversionTimeLeftSeconds - lenghtSAll,
      lenghtSAll
    );

    if (!validAudioLength) {
      clearInput();
    }
  }
};
